/**
 * THE ASK QUEUE — the screen this whole module exists for.
 *
 * Every row answers three questions in the order a rep needs them:
 *   how fresh is this          (the number, colour-coded)
 *   WHO actually has the rings (the guidance, one line)
 *   what is left to ask for    (the buttons)
 *
 * The middle one is the part a date alone gets wrong. A single bulk shipment
 * means one box reached one person; the athletes have not seen anything, so
 * the row says so rather than implying forty happy customers.
 *
 * ⚠️ WHAT CHANGED IN v2.41.0, AND WHY — measured on the live app 2026-09-11:
 * this tab rendered **274 cards at once**, 76,906px of page, 95 phone-screens
 * of scroll, 6,588 DOM nodes (heavy enough that a screenshot capture timed out
 * at 30s), with no search, no filter and no pagination. Every rep saw every
 * other rep's book, and the same three cautions — "team is not holding these
 * yet", "date INFERRED", "waiting on the ceremony" — were printed verbatim on
 * every single card. Written once they are a useful warning; printed 274 times
 * they are wallpaper, and the account name drowns in them.
 *
 * So: MINE is the default and firm-wide is one tap away; rows page 20 at a
 * time with a receipt that reconciles; and the cautions are FOLDED, not
 * dropped — one line that names how many there are, opening to the full text.
 * Nothing became unreachable, and no row is ever filtered out of firm-wide.
 */
import { useMemo, useState } from 'react'
import DraftPanel from './DraftPanel.jsx'
import CoveragePanel from './CoveragePanel.jsx'
import { identityOf, scopeQueue, pageOf, PAGE } from './reviewScope.js'
import { holdFor } from './reviewCoverage.js'
import s from './reviews.module.css'

const TYPE_LABEL = {
  internal: 'Internal', google: 'Google', facebook: 'Facebook',
  instagram: 'Instagram', tiktok: 'TikTok', x: 'X',
}
const TYPE_WHY = {
  internal: 'SR owns the words — usable in social and collateral forever.',
  google: 'Works while nobody is watching. This is the discovery one.',
  facebook: 'Proof to the team’s own community, where the parents already are.',
  instagram: 'The ring reveal — the most shareable thing SR ever gets.',
  tiktok: 'The box-opening video. Reaches past the school’s own following.',
  x: 'Travels fastest in college and pro circles.',
}

const STATE_WORD = { peak: 'Peak', warm: 'Still warm', closing: 'Cooling', missed: 'Missed' }

export default function AskQueue({ data, user, canAsk, onAsk, onSuppress, onReload }) {
  const [busy, setBusy] = useState(null)
  const [draft, setDraft] = useState(null)     // { orderId, type, account }
  const [scope, setScope] = useState('mine')   // 'mine' | 'firm'
  const [page, setPage] = useState(1)
  const [openNotes, setOpenNotes] = useState({})   // orderId -> bool
  const [suppressing, setSuppressing] = useState(null) // orderId
  const [reason, setReason] = useState('')

  const t = data?.tally || {}

  const identity = useMemo(() => identityOf(user), [user])
  // `data?.queue || []` stays INSIDE the memo — hoisting it to a const makes a
  // fresh array identity on every render and the memo never holds.
  const split = useMemo(() => scopeQueue(data?.queue || [], identity), [data, identity])

  // Firm-wide is ALWAYS the complete list — the scope tab never filters a row
  // out of the view whose job is to show everything.
  const active = scope === 'mine' ? split.mine : split.firm
  const view = pageOf(active, page, PAGE)

  const go = (next) => { setScope(next); setPage(1); setDraft(null) }

  const ask = async (orderId, type) => {
    setBusy(orderId + type)
    try { await onAsk(orderId, type); setDraft(null) } finally { setBusy(null) }
  }

  const confirmSuppress = (orderId) => {
    if (!reason.trim()) return
    onSuppress(orderId, reason.trim())
    setSuppressing(null); setReason('')
  }

  return (
    <div className={s.wrap}>

      {/* ── COVERAGE ────────────────────────────────────────────────────
          Was a red "2,178 · asks never made" headline plus a flat tally row.
          BOTH were wrong in the same direction: the headline aged records off
          bulk-edit timestamps and called the result a failure, and the tally
          mixed exclusive states with overlays while omitting `notDelivered`
          — the biggest single answer to "why isn't my client here".
          CoveragePanel owns the whole story now: the restated miss, the
          partition that reconciles, the overlays kept visibly apart, and the
          hold. See reviewCoverage.js for the measurement behind it. */}
      <CoveragePanel tally={t} queue={active} scope={scope} />

      <p className={s.rule}>
        Delivery is the only trigger. Nothing reaches this queue until the customer is
        physically holding the product, and payment never gates it — a school on net-30
        that took delivery on Friday is at peak excitement on Monday.
      </p>

      {data?.scopeResolved === false && (
        <div className={s.warn}>
          <strong>This queue is empty because nobody could be identified, not because
          there is nothing to ask for.</strong>{' '}
          {data.warning}
        </div>
      )}

      {data?.truncated && (
        <div className={s.warn}>
          The order board read hit its page cap, so this queue is incomplete. Reported
          rather than silently short.
        </div>
      )}

      {/* ── SCOPE ────────────────────────────────────────────────────────
          Mine lands. Firm-wide is one tap, never the default. */}
      <div className={s.scopeRow} role="tablist" aria-label="Whose queue">
        <button
          type="button" role="tab" aria-selected={scope === 'mine'}
          className={`${s.scopeBtn} ${scope === 'mine' ? s.scopeOn : ''}`}
          onClick={() => go('mine')}
        >
          Mine{split.resolved ? ` · ${split.mine.length}` : ''}
        </button>
        <button
          type="button" role="tab" aria-selected={scope === 'firm'}
          className={`${s.scopeBtn} ${scope === 'firm' ? s.scopeOn : ''}`}
          onClick={() => go('firm')}
        >
          Everyone · {split.firm.length}
        </button>
      </div>

      {/* The flat `.tally` row that stood here is gone — it is the defect
          CoveragePanel exists to fix, so leaving a second copy of the same
          eight numbers above the fold would reintroduce it verbatim. Every
          figure it carried is still on screen, inside a partition that
          reconciles. */}

      {/* An unresolvable identity is NEVER drawn as an empty queue. */}
      {scope === 'mine' && !split.resolved && (
        <div className={s.warn}>
          <strong>This list is empty because we could not tell who you are signed in
          as, not because you have nothing to ask for.</strong>{' '}
          Orders name their reps by display name and the signed-in account gave us{' '}
          {identity.label ? `“${identity.label}”` : 'nothing usable'} to match on.
          Everyone’s queue is one tap away above — all {split.firm.length} are there.
        </div>
      )}

      {scope === 'mine' && split.resolved && split.mineEmpty && (
        <div className={s.empty}>
          <strong>Nothing of yours to ask for today.</strong>
          <br />
          {split.firm.length} {split.firm.length === 1 ? 'order is' : 'orders are'} in
          the firm-wide queue. Yours is genuinely clear — this is not a failed lookup;
          we matched you and found no rows carrying your name.
        </div>
      )}

      {!active.length && !(scope === 'mine' && (!split.resolved || split.mineEmpty)) ? (
        <div className={s.empty}>
          <strong>Nothing to ask for today.</strong>
          <br />
          {t.tooEarly || t.inTransit ? (
            <>
              {(t.tooEarly || 0) + (t.inTransit || 0)} order
              {(t.tooEarly || 0) + (t.inTransit || 0) === 1 ? ' is' : 's are'} on the way or
              still settling. They will appear here on their own once the customer is
              holding them — asking sooner is the thing this queue exists to prevent.
            </>
          ) : (
            <>Every recent delivery has already been asked. An empty queue here means the
            work is done, not that nothing was looked at.</>
          )}
        </div>
      ) : null}

      {active.length > 0 && (
        <>
          <div className={s.list}>
            {view.rows.map((r) => {
              const rd = r.readiness || {}
              // The cautions, gathered rather than stacked. Each one is still
              // here in full — folded, never dropped.
              const notes = []
              if (rd.teamHasThem === false) notes.push('The team is not holding these yet — one box went to one person. The athlete-facing moment is the ring ceremony, and SR does not record that date anywhere today.')
              if (rd.unknownDeliveryType) notes.push('No delivery type on this order, so who is holding these is a guess. Confirm before you ask.')
              if (rd.deliveredFirm === false) notes.push('Delivery date INFERRED from the order status, not from an In Hands Date. The day count may be wrong.')
              if (r.blockedTypes?.length > 0) notes.push(`Waiting on the ceremony: ${r.blockedTypes.map((b) => TYPE_LABEL[b.type] || b.type).join(', ')}. ${r.blockedTypes[0].reason}`)
              const open = !!openNotes[r.orderId]
              /* HELD = we cannot confidently address this ask (no email, no
               * named contact, or an unknowable recipient). It is NOT a
               * judgement about the customer and NOT a do-not-ask — the row
               * stays, the buttons stay, and the chip names the missing field.
               * Suppression is a separate, human act carrying a reason. */
              const hold = holdFor(r)

              return (
                <article key={r.orderId} className={s.card} data-state={rd.state}>
                  <div className={s.head}>
                    <div className={s.headMain}>
                      <div className={s.account}>
                        {r.url ? (
                          <a href={r.url} target="_blank" rel="noopener noreferrer">{r.account}</a>
                        ) : r.account}
                      </div>
                      <div className={s.meta}>
                        {r.sport && <span>{r.sport}</span>}
                        {r.level && <span>· {r.level}</span>}
                        {r.rings ? <span>· {r.rings} rings</span> : null}
                        {r.reps?.length ? <span>· {r.reps.join(', ')}</span> : null}
                      </div>
                    </div>
                    <div className={`${s.when} ${s['state_' + rd.state] || ''}`}>
                      <div className={s.days}>{rd.daysSince === 0 ? 'today' : `${rd.daysSince}d`}</div>
                      <div className={s.whenLabel}>{STATE_WORD[rd.state] || ''}</div>
                    </div>
                  </div>

                  {rd.askWho && (
                    <div className={s.guidance}>
                      Ask <span className={s.askWho}>{rd.askWho}</span>. {rd.guidance}
                    </div>
                  )}

                  {hold.held && (
                    <div className={s.holdChip}>
                      <span className={s.holdTag}>Held</span>
                      {hold.reasons.map((x) => x.label).join(' · ')} — fill this in and it
                      is sendable. Nothing here says anything about the customer.
                    </div>
                  )}

                  {/* FOLDED, NOT HIDDEN. The count is on the summary, so a row
                      with three cautions looks different from a clean one at a
                      glance — which the stacked version could not do, because
                      every row looked the same. */}
                  {notes.length > 0 && (
                    <div className={s.notes}>
                      <button
                        type="button"
                        className={s.notesToggle}
                        aria-expanded={open}
                        onClick={() => setOpenNotes((m) => ({ ...m, [r.orderId]: !open }))}
                      >
                        {open ? 'Hide' : 'Read'} {notes.length} thing{notes.length === 1 ? '' : 's'} to know before you ask
                      </button>
                      {open && (
                        <ul className={s.notesList}>
                          {notes.map((n, i) => <li key={i}>{n}</li>)}
                        </ul>
                      )}
                    </div>
                  )}

                  {r.alreadyLanded?.length > 0 && (
                    <div className={s.landed}>
                      Already landed: {r.alreadyLanded.map((x) => TYPE_LABEL[x]).join(', ')}
                    </div>
                  )}

                  <div className={s.actions}>
                    {r.outstandingTypes.map((type) => (
                      <button
                        key={type}
                        type="button"
                        className={`${s.btn} ${s.ghost}`}
                        /* A real accessible name. The title attribute alone made
                           the a11y tree read these as "Works while nobody is
                           watching. This is the discovery one." — the rationale,
                           not the action. */
                        aria-label={`Draft the ${TYPE_LABEL[type]} review ask for ${r.account}`}
                        title={TYPE_WHY[type]}
                        disabled={busy === r.orderId + type}
                        onClick={() => setDraft({ orderId: r.orderId, type, account: r.account })}
                      >
                        {TYPE_LABEL[type]} <span aria-hidden="true">→</span>
                      </button>
                    ))}
                    {onSuppress && suppressing !== r.orderId && (
                      <button
                        type="button"
                        className={`${s.btn} ${s.quiet}`}
                        aria-label={`Flag ${r.account} as do-not-ask`}
                        onClick={() => { setSuppressing(r.orderId); setReason('') }}
                      >
                        Do not ask
                      </button>
                    )}
                  </div>

                  {/* ── THE GOVERNANCE ACTION ──────────────────────────────
                      This was a window.prompt(): a native dialog with no
                      reason validation, no statement of what it does, and no
                      undo. Flagging an account do-not-ask takes it out of every
                      rep's queue, so it gets a real confirm that captures the
                      reason and states the undo path in words. */}
                  {onSuppress && suppressing === r.orderId && (
                    <div className={s.confirm} role="group" aria-label={`Flag ${r.account} as do-not-ask`}>
                      <div className={s.confirmTitle}>Stop everyone asking {r.account}?</div>
                      <p className={s.confirmWhat}>
                        This removes the account from every rep’s queue, not just yours.
                        The reason is stored on the record so the next person can see why.
                      </p>
                      <label className={s.fieldLabel} htmlFor={`why-${r.orderId}`}>
                        Why — a remake, a missing ring, a complaint
                      </label>
                      <textarea
                        id={`why-${r.orderId}`}
                        className={s.textarea}
                        rows={2}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Two rings came back for resizing and the coach is not happy yet."
                      />
                      <p className={s.confirmUndo}>
                        Reversible. Confirming shows an <strong>Undo</strong> for the next
                        few seconds, and the flag can be lifted from the record at any
                        time afterwards — nothing here is deleted.
                      </p>
                      <div className={s.actions}>
                        <button
                          type="button"
                          className={s.btn}
                          disabled={!reason.trim()}
                          onClick={() => confirmSuppress(r.orderId)}
                        >
                          Flag do-not-ask
                        </button>
                        <button
                          type="button"
                          className={`${s.btn} ${s.ghost}`}
                          onClick={() => { setSuppressing(null); setReason('') }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {draft?.orderId === r.orderId && (
                    <DraftPanel
                      orderId={draft.orderId}
                      type={draft.type}
                      account={draft.account}
                      onClose={() => setDraft(null)}
                      onRecordAsk={canAsk ? () => ask(draft.orderId, draft.type) : null}
                    />
                  )}
                </article>
              )
            })}
          </div>

          {/* The receipt reconciles, always. */}
          <div className={s.receipt}>
            Showing <b>{view.shown}</b> of <b>{view.total}</b>
            {scope === 'mine' ? ' of yours' : ' firm-wide'}
            {view.hasMore ? ` — ${view.remaining} more` : ''}
          </div>

          {view.hasMore && (
            <div className={s.actions}>
              <button type="button" className={`${s.btn} ${s.ghost}`} onClick={() => setPage((p) => p + 1)}>
                Show {Math.min(PAGE, view.remaining)} more
              </button>
              <button
                type="button" className={`${s.btn} ${s.quiet}`}
                onClick={() => setPage(Math.ceil(view.total / PAGE))}
              >
                Show all {view.total}
              </button>
            </div>
          )}
        </>
      )}

      <p className={s.note}>
        Ordered peak first, and inside a band the freshest first — the earlier ask is
        always the better one. An order drops off after about 45 days and is counted as
        missed rather than quietly disappearing.
      </p>

      <button type="button" className={`${s.btn} ${s.ghost}`} onClick={onReload}>
        Refresh
      </button>
    </div>
  )
}
